/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Configuration } from '@azure/msal-browser';
import { isPlatformMobile, getPlatform } from '../../util/platform';

const nativeRedirectUri = () =>
  getPlatform() === 'android'
    ? process.env.REACT_APP_ANDROID_REDIRECT_SCHEME
    : process.env.REACT_APP_IOS_REDIRECT_SCHEME;

const getNativeConfig = () => ({
  redirectUri: nativeRedirectUri(),
  postLogoutRedirectUri: nativeRedirectUri(),
});

const webConfig = {
  redirectUri: process.env.REACT_APP_AZURE_B2C_LOGIN_REDIRECT_URI,
  postLogoutRedirectUri: process.env.REACT_APP_AZURE_B2C_LOGOUT_REDIRECT_URI,
};

export const passwordResetConfig = {
  authority: process.env.REACT_APP_AZURE_B2C_AUTHORITY_PASSWORD_RESET,
  scopes: [`${process.env.REACT_APP_REQUEST_API_SCOPES}`],
};

const knownAuthorities = [
  process.env.REACT_APP_AZURE_B2C_AUTHORITY!,
  process.env.REACT_APP_AZURE_B2C_AUTHORITY_RESET!,
];

const knownCEAuthorities = [
  process.env.REACT_APP_AZURE_CE_B2C_AUTHORITY!,
  process.env.REACT_APP_AZURE_B2C_AUTHORITY_RESET!,
];

const getRedirectUris = () => {
  return isPlatformMobile() ? getNativeConfig() : webConfig;
};
const CE_LINK_URI = process.env.REACT_APP_AZURE_CE_B2C_LINK_URI;

// Check if current route contains 'CE' or if MSAL tokens exist in session storage
const isCEUser = (): boolean => {
  if (typeof window !== 'undefined') {
    // Check if route contains 'CE'
    if (window.location.pathname.includes(CE_LINK_URI || 'ce-link')) {
      return true;
    }

    // Check if MSAL tokens already exist in session storage
    const hasMsalTokensInSessionStorage = () => {
      try {
        const sessionStorageKeys = Object.keys(sessionStorage);
        return sessionStorageKeys.some((key) =>
          key.startsWith(`msal.token.keys.${process.env.REACT_APP_AZURE_CE_B2C_CLIENT_ID}`),
        );
      } catch (error) {
        return false;
      }
    };
    if (hasMsalTokensInSessionStorage()) {
      return true;
    }
  }
  return false;
};

export const getMsalConfiguration = (): Configuration => {
  if (!isPlatformMobile() && isCEUser()) {
    return {
      auth: {
        clientId: process.env.REACT_APP_AZURE_CE_B2C_CLIENT_ID!,
        authority: process.env.REACT_APP_AZURE_CE_B2C_AUTHORITY,
        knownAuthorities: knownCEAuthorities,
        redirectUri: process.env.REACT_APP_AZURE_CE_B2C_LOGIN_REDIRECT_URI,
        postLogoutRedirectUri: process.env.REACT_APP_AZURE_CE_B2C_LOGOUT_REDIRECT_URI,
      },
      cache: {
        cacheLocation: 'sessionStorage',
        storeAuthStateInCookie: false,
      },
      system: {
        allowNativeBroker: false, // Disables WAM Broker
      },
    };
  }
  return {
    auth: {
      clientId: process.env.REACT_APP_AZURE_B2C_CLIENT_ID!,
      authority: process.env.REACT_APP_AZURE_B2C_AUTHORITY,
      knownAuthorities,
      ...getRedirectUris(), // login logout according to app
    },
    cache: {
      cacheLocation: 'localStorage',
      storeAuthStateInCookie: false,
    },
    system: {
      allowNativeBroker: false, // Disables WAM Broker
    },
  };
};
export const scopes = [];
